import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import LogoMark from '../../../components/ui/LogoMark';

const HeroSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="relative overflow-hidden py-20 lg:py-28">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-slate-50"></div>
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/5 to-transparent"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
              <Icon name="Award" size={16} />
              <span>{t('landing.hero.badge')}</span>
            </div>

            {/* Main Heading */}
            <div className="space-y-6">
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-foreground leading-tight break-words">
                {t('landing.hero.titleLine1')}
                <span className="text-primary block">{t('landing.hero.titleLine2')}</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                {t('landing.hero.description')}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-4 bg-accent text-white rounded-lg font-semibold hover:bg-accent/90 transition-smooth flex items-center justify-center space-x-2 shadow-sm"
              >
                <Icon name="ArrowRight" size={16} />
                <span>{t('landing.hero.ctaSignIn')}</span>
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-4 border-2 border-primary text-primary rounded-lg font-semibold hover:bg-primary/5 transition-smooth flex items-center justify-center space-x-2"
              >
                <Icon name="Play" size={16} />
                <span>{t('landing.hero.ctaDemo')}</span>
              </button>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative">
            {/* Dashboard Preview Card */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 transform rotate-3 hover:rotate-0 transition-transform duration-300">
              {/* Mock Dashboard Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <LogoMark size={24} />
                  <span className="font-semibold text-foreground">{t('landing.hero.card')}</span>
                </div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>

              {/* Mock Chart */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">{t('landing.hero.cardMetricGap')}</span>
                  <span className="text-sm font-bold text-success">-2.3%</span>
                </div>
                
                {/* Progress Bars */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-muted-foreground w-16">Q1</span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div className="bg-primary rounded-full h-2 w-3/4"></div>
                    </div>
                    <span className="text-xs font-medium">84%</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-muted-foreground w-16">Q2</span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div className="bg-primary rounded-full h-2 w-4/5"></div>
                    </div>
                    <span className="text-xs font-medium">92%</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-muted-foreground w-16">Q3</span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div className="bg-success rounded-full h-2 w-full"></div>
                    </div>
                    <span className="text-xs font-medium">98%</span>
                  </div>
                </div>

                {/* Mock Metrics */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-primary/5 rounded-lg p-3">
                    <div className="text-lg font-bold text-primary">1,247</div>
                    <div className="text-xs text-muted-foreground">{t('landing.hero.cardMetricEmployees')}</div>
                  </div>
                  <div className="bg-success/5 rounded-lg p-3">
                    <div className="text-lg font-bold text-success">97.4%</div>
                    <div className="text-xs text-muted-foreground">{t('landing.hero.cardMetricCompliance')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-success text-white p-3 rounded-full shadow-lg">
              <Icon name="CheckCircle" size={16} />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white p-3 rounded-full shadow-lg">
              <Icon name="TrendingUp" size={16} className="text-primary" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;